<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Legal</p>
        <h1 class="info-h1">Privacy<br><em>Policy</em></h1>
        <p class="info-lead">
          We are committed to protecting your privacy and the privacy of children using our platform.
          Last updated: <strong>1 January 2025</strong>.
        </p>
      </div>
    </div>

    <div class="info-body legal-body">

      <div class="legal-toc">
        <h3 class="legal-toc__title">Contents</h3>
        <ol class="legal-toc__list">
          <li><a href="#who-we-are">1. Who We Are</a></li>
          <li><a href="#data-we-collect">2. Data We Collect</a></li>
          <li><a href="#how-we-use">3. How We Use Your Data</a></li>
          <li><a href="#children">4. Children's Privacy</a></li>
          <li><a href="#legal-basis">5. Legal Basis for Processing</a></li>
          <li><a href="#sharing">6. Sharing Your Data</a></li>
          <li><a href="#retention">7. Data Retention</a></li>
          <li><a href="#your-rights">8. Your Rights</a></li>
          <li><a href="#cookies">9. Cookies &amp; Tracking</a></li>
          <li><a href="#security">10. Security</a></li>
          <li><a href="#changes">11. Changes to This Policy</a></li>
          <li><a href="#contact">12. Contact Us</a></li>
        </ol>
      </div>

      <section id="who-we-are" class="legal-section">
        <h2 class="legal-h2">1. Who We Are</h2>
        <p>Maths Eleven Plus ("we", "us", "our") operates the website at <strong>mathselevenplus.co.uk</strong> and the associated learning platform. We are registered in England and Wales.</p>
        <p>We act as the <strong>data controller</strong> for the personal data described in this policy. If you have any questions, contact us at <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a>.</p>
      </section>

      <section id="data-we-collect" class="legal-section">
        <h2 class="legal-h2">2. Data We Collect</h2>

        <h3 class="legal-h3">Account data (parent / guardian)</h3>
        <ul class="legal-list">
          <li>Name and email address provided at registration</li>
          <li>Password (stored as a secure hash — never in plain text)</li>
          <li>Subscription plan and billing history (processed via Stripe)</li>
          <li>IP address and browser / device type at login</li>
        </ul>

        <h3 class="legal-h3">Child profile data</h3>
        <ul class="legal-list">
          <li>First name or nickname (provided by parent)</li>
          <li>Year group or target exam year (optional)</li>
          <li>We do <strong>not</strong> collect a child's surname, date of birth, school, or contact details</li>
        </ul>

        <h3 class="legal-h3">Learning &amp; usage data</h3>
        <ul class="legal-list">
          <li>Questions answered, accuracy, time spent, and mastery band progression</li>
          <li>Diagnostic assessment results and revision history</li>
          <li>Feature interactions (e.g. which sections are visited) — used to improve the platform</li>
        </ul>

        <h3 class="legal-h3">Payment data</h3>
        <p>Payments are processed by <strong>Stripe</strong>. We do not store card numbers or full payment details on our servers. Stripe's privacy policy is available at <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener">stripe.com/gb/privacy</a>.</p>
      </section>

      <section id="how-we-use" class="legal-section">
        <h2 class="legal-h2">3. How We Use Your Data</h2>
        <ul class="legal-list">
          <li><strong>Provide the service</strong> — authenticate accounts, store and display learning progress, generate practice sessions.</li>
          <li><strong>Personalise learning</strong> — adapt question difficulty and topic selection based on performance.</li>
          <li><strong>Billing &amp; subscriptions</strong> — process payments, issue receipts, manage plan changes.</li>
          <li><strong>Transactional emails</strong> — account confirmation, password reset, subscription receipts. We do not send unsolicited marketing without consent.</li>
          <li><strong>Platform improvement</strong> — aggregate, anonymised analytics to understand how features are used and improve them.</li>
          <li><strong>Legal compliance</strong> — retain records as required by applicable law.</li>
        </ul>
      </section>

      <section id="children" class="legal-section">
        <h2 class="legal-h2">4. Children's Privacy</h2>
        <p>Our platform is designed for children aged 9–11, always under the supervision of a parent or guardian. We take children's privacy seriously and comply with the <strong>UK GDPR</strong> and the <strong>Children's Code (Age Appropriate Design Code)</strong>.</p>
        <ul class="legal-list">
          <li>A child account can only be created by a registered parent or guardian.</li>
          <li>We collect the minimum data necessary for a child profile — only a first name or nickname.</li>
          <li>We do not show advertising to children.</li>
          <li>We do not share children's learning data with third parties for marketing or profiling purposes.</li>
          <li>Parents can view, correct, or delete their child's data at any time by contacting us.</li>
        </ul>
      </section>

      <section id="legal-basis" class="legal-section">
        <h2 class="legal-h2">5. Legal Basis for Processing</h2>
        <ul class="legal-list">
          <li><strong>Contract</strong> — processing necessary to provide the service you signed up for (account management, learning progress, billing).</li>
          <li><strong>Legitimate interests</strong> — platform analytics and security monitoring, where our interests do not override your rights.</li>
          <li><strong>Legal obligation</strong> — retaining financial records as required by HMRC.</li>
          <li><strong>Consent</strong> — optional analytics cookies, where you have explicitly agreed via the cookie banner.</li>
        </ul>
      </section>

      <section id="sharing" class="legal-section">
        <h2 class="legal-h2">6. Sharing Your Data</h2>
        <p>We do not sell your personal data. We share data only with the following trusted third parties, under strict data processing agreements:</p>
        <ul class="legal-list">
          <li><strong>Stripe</strong> — payment processing (UK/EU data centres)</li>
          <li><strong>Amazon Web Services (AWS)</strong> — cloud hosting and database storage (EU West — Ireland)</li>
          <li><strong>Email provider</strong> — transactional emails only (no marketing lists)</li>
        </ul>
        <p>We may also disclose data if required by law or to protect the rights, property, or safety of Maths Eleven Plus, our users, or the public.</p>
      </section>

      <section id="retention" class="legal-section">
        <h2 class="legal-h2">7. Data Retention</h2>
        <ul class="legal-list">
          <li><strong>Active accounts</strong> — data is retained for as long as the account is active.</li>
          <li><strong>After account deletion</strong> — personal data is deleted within 30 days. Anonymised learning statistics may be retained for platform improvement.</li>
          <li><strong>Financial records</strong> — billing records are retained for 7 years as required by UK tax law.</li>
        </ul>
      </section>

      <section id="your-rights" class="legal-section">
        <h2 class="legal-h2">8. Your Rights</h2>
        <p>Under UK GDPR you have the following rights regarding your personal data:</p>
        <ul class="legal-list">
          <li><strong>Access</strong> — request a copy of the data we hold about you.</li>
          <li><strong>Rectification</strong> — ask us to correct inaccurate or incomplete data.</li>
          <li><strong>Erasure</strong> — request deletion of your data ("right to be forgotten").</li>
          <li><strong>Restriction</strong> — ask us to limit how we process your data in certain circumstances.</li>
          <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
          <li><strong>Objection</strong> — object to processing based on legitimate interests.</li>
          <li><strong>Withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time.</li>
        </ul>
        <p>To exercise any of these rights, email <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a>. We will respond within 30 days. You also have the right to lodge a complaint with the <strong>Information Commissioner's Office (ICO)</strong> at <a href="https://ico.org.uk" target="_blank" rel="noopener">ico.org.uk</a>.</p>
      </section>

      <section id="cookies" class="legal-section">
        <h2 class="legal-h2">9. Cookies &amp; Tracking</h2>
        <p>We use essential cookies to keep you signed in, and optional analytics cookies to understand how the platform is used. You can manage your cookie preferences at any time via the cookie banner or our <router-link to="/info/cookies">Cookie Policy</router-link>.</p>
      </section>

      <section id="security" class="legal-section">
        <h2 class="legal-h2">10. Security</h2>
        <p>We implement industry-standard security measures including HTTPS encryption, hashed passwords, and access controls. However, no system is completely secure. If you believe your account has been compromised, contact us immediately at <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a>.</p>
      </section>

      <section id="changes" class="legal-section">
        <h2 class="legal-h2">11. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. When we do, we will update the "last updated" date at the top. For significant changes, we will notify registered users by email. Continued use of the service after changes are posted constitutes acceptance of the updated policy.</p>
      </section>

      <section id="contact" class="legal-section">
        <h2 class="legal-h2">12. Contact Us</h2>
        <p>For any privacy-related questions or to exercise your rights:</p>
        <ul class="legal-list">
          <li>Email: <a href="mailto:privacy@mathselevenplus.co.uk">privacy@mathselevenplus.co.uk</a></li>
        </ul>
      </section>

    </div>
  </div>
</template>

<script setup>
// No script needed — static legal page
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }
.info-hero { background: #0D1628; padding: 5rem 2rem 4rem; }
.info-hero__inner { max-width: 52rem; margin: 0 auto; }
.info-eyebrow { font-size: 0.8rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #B8902A; margin-bottom: 1rem; }
.info-h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(2.25rem, 5vw, 3.25rem); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 1.25rem; }
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead { font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 44rem; line-height: 1.7; }

.info-body { max-width: 52rem; margin: 0 auto; padding: 3rem 2rem 4rem; }

/* TOC */
.legal-toc { background: #fff; border: 1px solid #e5e7eb; border-left: 4px solid #B8902A; border-radius: 0.75rem; padding: 1.5rem 2rem; margin-bottom: 2.5rem; }
.legal-toc__title { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #B8902A; margin-bottom: 0.75rem; }
.legal-toc__list { margin: 0; padding-left: 1.25rem; display: flex; flex-direction: column; gap: 0.375rem; }
.legal-toc__list li { font-size: 0.875rem; }
.legal-toc__list a { color: #374151; text-decoration: none; transition: color 0.15s; }
.legal-toc__list a:hover { color: #B8902A; }

.legal-section { margin-bottom: 2.5rem; scroll-margin-top: 80px; }
.legal-h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; font-weight: 700; color: #0D1628; margin-bottom: 0.875rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
.legal-h3 { font-size: 0.95rem; font-weight: 700; color: #0D1628; margin-top: 1.25rem; margin-bottom: 0.5rem; }
.legal-section p { font-size: 0.9rem; line-height: 1.8; color: #374151; margin-bottom: 0.75rem; }
.legal-section a { color: #B8902A; }
.legal-section a:hover { color: #0D1628; }
.legal-list { margin: 0.5rem 0 0.75rem 1.5rem; display: flex; flex-direction: column; gap: 0.4rem; }
.legal-list li { font-size: 0.9rem; line-height: 1.7; color: #374151; }
</style>
